/**
 * Employee Time Portal (P-TAS-20 Phase 3) — the /portal API shapes the portal reads.
 * Kept free of axios so the pure model and its jest tests can import them. The field
 * names are the API's (camelCase, as the mobile routers answer); see C:\github\API
 * src/controllers/portal/*.ts, src/dbInterfaces/TimeEntryHourly.ts and src/routes/timeOff.ts.
 */

/** TimeEntryHourly.Type. */
export const TYPE_REGULAR = 1;
export const TYPE_MEAL = 2;
/** PunchType: 1 = from the punch button. */
export const PUNCH_TYPE_BUTTON = 1;

/** One TimeEntryHourly row as /portal answers it (TimeEntryHourlyResponse). Dates arrive as UTC ISO strings. */
export interface PortalHourlyRow {
  timeEntryHourlyID: number;
  employeeID: number;
  payPeriodID: number;
  inDateTime: string;
  inDateTimeOffset: string;
  outDateTime: string | null;
  outDateTimeOffset: string | null;
  type: number;
  punchTypeIn: number;
  punchTypeOut: number | null;
  project?: { projectID: number | null; altProjectID?: string | null; name?: string | null } | null;
  account?: { accountCodeID: number | null; altAccountCodeID?: string | null; name?: string | null; description?: string | null } | null;
  projectPhase?: { projectPhaseID: number | null; altProjectPhaseID?: string | null; name?: string | null; description?: string | null } | null;
  serviceOrderText?: string | null;
  customerServiceWarranty?: { customerServiceWarrantyID: number | null } | null;
  locationName?: string | null;
  description?: string | null;
  inLatitude?: number | null;
  inLongitude?: number | null;
  outLatitude?: number | null;
  outLongitude?: number | null;
  serviceOrder?: { serviceOrderID: number } | null;
  serviceOrderScopeID?: number | null;
  tempServiceOrderDescription?: string | null;
  /** 'PAYROLL' | 'EMPLOYEE' | 'KIOSK' | 'WEB' | 'SYSTEM'; null = older than the provenance columns (mobile). */
  createdSource?: string | null;
  modifiedSource?: string | null;
}

/** The parts of the employee's Role the portal reads (GET /portal/self → role). */
export interface PortalRole {
  roleID: number;
  name: string;
  stateAbbr?: string | null;
  recordHours?: boolean;
  recordHoursJob?: boolean;
  recordHoursOther?: boolean;
  defaultAccountCodeID?: number | null;
  allowTimeOffRequests?: boolean;
  office?: boolean;
  /** Claims the portal can't read: a role recording any of them submits from mobile. */
  recordTasks?: boolean;
  recordOperationalTask?: boolean;
  recordCustomerServiceWarranty?: boolean;
}

/** GET /portal/self — the mobile /self payload plus the portal additions. */
export interface PortalSelf {
  employeeID: number;
  altEmployeeID: string;
  name: { first: string; middle?: string | null; last: string };
  timeEntriesHourly: PortalHourlyRow[];
  submitted: boolean;
  role: PortalRole | null;
  supervisorID: number | null;
  payPeriodID: number | null;
  openPunchHourly: PortalHourlyRow | null;
  selfEmployee: { employeeID: number; firstName: string; lastName: string; roleName: string | null };
  salaried: boolean | null;
}

/** GET /portal/timesheet/status?payPeriodID= */
export interface PortalTimesheetStatus {
  payPeriodID: number;
  submitted: boolean;
  approvalStatus: number | null;
  submittedAt: string | null;
  submittedAtOffset: string | null;
  supervisorApprovedAt: string | null;
  payrollApprovedAt: string | null;
  exported: boolean;
  exportedAt: string | null;
  locked: boolean;
  supervisorName: string | null;
  noSupervisor: boolean;
  /** The submitted answers ("Yes", "No", text, a waiver's "Yes - 10/13/26"); empty before submit, absent from an older API. */
  answers?: PortalTimesheetAnswer[];
}

/** A pay period from /portal/payPeriods/getAroundDate, normalized to `YYYY-MM-DD` dates. */
export interface PortalPayPeriod {
  payPeriodID: number;
  startYmd: string;
  endYmd: string;
  checkYmd: string | null;
}

/** GET /portal/projects/recent */
export interface PortalRecentProject {
  projectID: number | null;
  altProjectID: string | null;
  projectName: string | null;
  projectPhaseID: number | null;
  altProjectPhaseID: string | null;
  projectPhaseName: string | null;
  accountCodeID: number | null;
  altAccountCodeID: string | null;
  accountCodeName: string | null;
  accountCodeDescription: string | null;
  lastUsedAt: string;
  useCount: number;
}

/** GET /portal/projects/search (ProjectSearchResult, the fields the picker reads). */
export interface PortalProjectSearchResult {
  projectID: number;
  altProjectID: string;
  name: string;
}

/** GET /portal/projects/:id/getActivePhases */
export interface PortalPhase {
  projectPhaseID: number;
  altProjectPhaseID: string;
  projectID: number;
  name: string;
  description: string | null;
}

/** GET /portal/projects/:projectId/phases/:phaseId/getAccountCodes */
export interface PortalAccountCode {
  accountCodeID: number;
  altAccountCodeID: string;
  name: string;
  description: string | null;
}

/** GET /portal/timeOff/holidays?startDate&endDate */
export interface PortalHoliday {
  holidayCalendarID: number;
  holidayDate: string;
  name: string;
  paidHoursIfNotWorked: number;
  eligibility: {
    isEligible: boolean;
    /** 'tenure' | 'dayBefore' | 'dayAfter' | null */
    reason?: string | null;
    tenure?: { status: string; eligibleOn: string | null; hireDateKnown?: boolean; eligibilityDays: number | null } | null;
    dayBefore?: { date: string | null; status: string } | null;
    dayAfter?: { date: string | null; status: string } | null;
  } | null;
}

/** GET /portal/timeOff/requests (TimeOffRequestWithDays). Today reads the first block; Time Off reads all of it. */
export interface PortalTimeOffRequest {
  timeOffRequestID: number;
  categoryName: string;
  startDate: string;
  endDate: string;
  totalHours: number;
  status: string;
  isUnpaid?: boolean;
  days?: { date: string; hours: number }[];
  timeOffCategoryID?: number;
  notes?: string | null;
  submittedAt?: string | null;
  /** The latest decision (approve / deny / hours modified) — null while nobody has acted. */
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  reviewerNotes?: string | null;
}

/** A paid category still holding time, which keeps Unpaid Time from being requested (TAS-586). */
export interface PortalTimeOffBlocking {
  categoryID: number;
  categoryName: string;
  reason: string;
  availableHours: number;
  thresholdHours: number;
}

/**
 * GET /portal/timeOff/balance — one row per category on the employee's policy.
 * `currentBalanceHours` is ALREADY NET of pending requests (the API deducts at submit and
 * refunds on deny / cancel); `pendingHours` is shown beside it, never subtracted again.
 */
export interface PortalTimeOffBalance {
  timeOffCategoryID: number;
  categoryName: string;
  sortOrder?: number;
  currentBalanceHours: number;
  pendingHours?: number;
  maxAnnualUsageHours: number | null;
  annualUsedHours?: number;
  annualRemainingHours: number | null;
  eligibilityDays: number | null;
  eligibleOn: string | null;
  /** As of TODAY; the submit gate judges tenure at the earliest requested day. */
  isEligible: boolean;
  eligibilityJudgedAt?: string;
  isUnpaid?: boolean;
  isRequestable?: boolean;
  notRequestableReason?: string | null;
  blocking?: PortalTimeOffBlocking[];
}

/** GET /portal/timeOff/requestOptions?startDate&endDate */
export interface PortalTimeOffRequestOptions {
  categories: PortalTimeOffBalance[];
  /** Company holidays in the range (not requestable unless allowWeekendHolidayDays). */
  excludedDates: { date: string; reason: string }[];
  /** First day a request may start (the past-date window); null = today. */
  earliestRequestDate: string | null;
  /** Past days covered by a submitted timesheet. */
  blockedPastDates: string[];
  eligibilityJudgedAt?: string;
  /** Older APIs omit it (treated as false here). */
  allowWeekendHolidayDays?: boolean;
}

/** POST /portal/timeOff/requests body. */
export interface PortalTimeOffRequestBody {
  timeOffCategoryID: number;
  days: { date: string; hours: number }[];
  notes: string;
}

/** One row of GET /portal/timeOff/ledger/:categoryId. */
export interface PortalTimeOffLedgerEntry {
  /** ISO date-time (pending rows: the request's start date). */
  date: string;
  kind: 'accrual' | 'usage' | 'cashout' | 'carryover' | 'adjustment' | 'pending' | 'other' | string;
  label: string;
  /** Signed delta. */
  hours: number;
  /** Null for pending rows (no balance effect yet). */
  balanceAfter: number | null;
  operationType: string | null;
  notes: string | null;
  timeOffRequestID: number | null;
}

export interface PortalTimeOffLedgerPage {
  currentBalanceHours: number;
  pending: PortalTimeOffLedgerEntry[];
  items: PortalTimeOffLedgerEntry[];
  total: number;
  offset: number;
  limit: number;
}

/** POST /portal/timeEntryHourly response. */
export interface PortalPunchAddResponse {
  success: boolean;
  timeEntryHourlyID?: number;
  payPeriodID?: number;
  timeEntriesHourly?: PortalHourlyRow[];
  replayed?: boolean;
}

/** POST /portal/timeEntryHourly/:id/switch response. */
export interface PortalSwitchResponse {
  success: true;
  closedTimeEntryHourlyID: number;
  openTimeEntryHourlyID: number;
  payPeriodID: number;
  timeEntriesHourly: PortalHourlyRow[];
  previousPayPeriodID: number | null;
  previousTimeEntriesHourly: PortalHourlyRow[] | null;
  replayed: boolean;
}

export type PunchSwitchKind = 'mealStart' | 'mealEnd' | 'jobSwitch';

/** What a punch is charged to ("Working on"). No project = non-job time ("Office / Overhead"). */
export interface PortalJob {
  projectID: number | null;
  projectPhaseID: number | null;
  accountCodeID: number | null;
  projectCode?: string | null;
  projectName?: string | null;
  phaseCode?: string | null;
  phaseName?: string | null;
  costCode?: string | null;
  costName?: string | null;
}

/* ------------------------------------------------------------------ My Timesheet (App-2) */

/** PunchType: 2 = a missing punch added by hand (mobile ManualAdd), 3 = an edited side (ManualEdit). */
export const PUNCH_TYPE_MANUAL_ADD = 2;
export const PUNCH_TYPE_MANUAL_EDIT = 3;

/** A TimeEntry (job / piece) row as GET /portal/timesheet returns it — only the ID is read. */
export interface PortalTimeEntryRow {
  timeEntryID: number;
}

/** GET /portal/timesheet?employeeID&payPeriodID (controllers/timesheet.ts getTimesheet). */
export interface PortalTimesheetResponse {
  submitted: boolean;
  timesheetID?: number;
  submittedDate?: string;
  submittedDateOffset?: string;
  payPeriodID: number;
  employeeID: number;
  timeEntries: PortalTimeEntryRow[];
  timeEntriesHourly: PortalHourlyRow[];
  signatureType?: string;
  payrollModified?: boolean;
}

/** One day of POST /portal/timesheet/getTimeSummary. `workDate` is `YYYY-MM-DD`. */
export interface PortalTimeSummaryDay {
  workDate: string;
  regularMinutes: number;
  overtimeMinutes: number;
  doubleTimeMinutes: number;
  holidayMinutes: number;
  sickMinutes: number;
  vacationMinutes: number;
  /** Worked + sick + vacation (what mobile shows as the day's total). */
  totalWorkMinutes: number;
  unpaidMinutes?: number;
}

export interface PortalTimeSummary {
  employeeID: number;
  payPeriodID: number;
  days: PortalTimeSummaryDay[];
  totals: Omit<PortalTimeSummaryDay, 'workDate'>;
}

/** dbo.TimesheetQuestions.AnswerType (API dbInterfaces/TimesheetQuestions.ts). */
export const ANSWER_YES_NO = 1;
export const ANSWER_DATE = 2;
export const ANSWER_CONFIRM_CHECKBOX = 3;
export const ANSWER_MEAL_WAIVER = 4;
export const ANSWER_COMMENTS = 5;

export interface PortalTimesheetQuestionChild {
  timesheetQuestionID: number;
  question: string;
  question_es?: string | null;
  answerType: number;
  order: number;
  /** The parent answer that shows this child ("Yes" when blank — mobile DEFAULT_ANSWER_TRIGGER). */
  answerTrigger: string | null;
  requiredWhenQuestionID: number | null;
  requiredWhenAnswer: string | null;
}

/** GET /portal/timesheetQuestions */
export interface PortalTimesheetQuestion {
  timesheetQuestionID: number;
  question: string;
  question_es?: string | null;
  answerType: number;
  order: number;
  requiredWhenQuestionID: number | null;
  requiredWhenAnswer: string | null;
  children: PortalTimesheetQuestionChild[];
}

/** POST /portal/timesheet/mealWaiverDates — dates are M/D/YY (mobile formatDateMonthDaySmallYear). */
export interface PortalMealWaiverDates {
  waiverDates: string[];
  penaltyDates: string[];
  perDay: { workDate: string; displayDate: string; hasWaiver: boolean; hasPenalty: boolean; missingPunch: boolean }[];
}

export interface PortalTimesheetAnswer {
  timesheetQuestionID: number;
  answer: string;
}

/** POST /portal/timesheet — field-for-field the body mobile sends (TimesheetManager.submitTimesheet). */
export interface PortalTimesheetSubmitBody {
  employeeID: number;
  payPeriodID: number;
  submittedDate: string;
  submittedDateOffset: string;
  timesheetAnswers: PortalTimesheetAnswer[];
  signature: string;
  timeEntryIDs: number[];
  employeeTaskIDs: number[];
  miscEmployeeTaskIDs: number[];
  timeEntryHourlyIDs: number[];
  employeeOperationalTaskIDs: number[];
  employeeCustomerServiceWarrantyTaskIDs: number[];
  employeeSickClaimIDs: number[];
  driveTimeEntryIDs: number[];
  employeeWorkOrderTaskIDs: number[];
}

/* ------------------------------------------------------------------ activity (App-4) */

/** GET /portal/activity — what happened (API controllers/portal/activity.ts ActivityKind). */
export type PortalActivityKind = 'punch' | 'edit' | 'submit' | 'approve' | 'unlock' | 'timeoff';
/** Who did it, relative to the signed-in employee. */
export type PortalActivityActorRole = 'you' | 'payroll' | 'supervisor' | 'system' | 'kiosk';
/** Where it came from. 'employee' = a submit / time-off request by you (web or mobile, not recorded). */
export type PortalActivitySourceType = 'web' | 'mobile' | 'kiosk' | 'payroll' | 'system' | 'supervisor' | 'employee';

export interface PortalActivityItem {
  /** Stable across calls (e.g. "punch:H123:ev4", "timeoff:42:submitted"). */
  id: string;
  /** ISO instant. */
  at: string;
  kind: PortalActivityKind;
  what: string;
  detail: string;
  actorName: string;
  actorRole: PortalActivityActorRole;
  source: { type: PortalActivitySourceType; label: string };
  delta?: { before: string; after: string };
  /** The pay period's first day (YYYY-MM-DD) the event belongs to. */
  weekMonday?: string;
}

/** GET /portal/activity?payPeriodID=&weeks=1..8 — newest first; payPeriodIDs = the weeks read, newest first. */
export interface PortalActivityResponse {
  items: PortalActivityItem[];
  payPeriodIDs: number[];
}
